import React from 'react'
import { useQuery } from 'react-query'
import { Html } from '@thirstycamel/ui'

import { backend } from '../../utils/backend'
import { cmsUseNew } from '../../utils/cms'
import { faqBlocksHaveItems, parseFaqMarkdown } from '../../utils/faqMarkdown'
import FaqList, { FaqItem, FaqListVariant } from '../FaqList'
import { FaqsDocument, FaqsQuery } from '../../../generated'
import { fetchWpGraphQL } from '../../utils/wpGraphql'

interface CoreFaq {
  key: string
  markdown: string
}

export function FaqSection({
  keyName,
  pageSlug,
  pageTitle,
  variant = 'dark',
  fallbackFaqs,
  hideTitle,
}: {
  keyName?: string
  /** When set, looks up FAQ by CMS page slug and optional title instead of exact key. */
  pageSlug?: string
  pageTitle?: string
  variant?: FaqListVariant
  fallbackFaqs?: FaqItem[]
  hideTitle?: boolean
}) {
  const useNew = cmsUseNew()
  const lookupSlug = pageSlug ?? keyName

  const { data, isFetching, isError } = useQuery<CoreFaq | FaqsQuery>(
    useNew
      ? pageSlug
        ? ['faqs/public/for-page', pageSlug, pageTitle]
        : ['faqs/public', keyName]
      : ['wp', FaqsDocument, { first: 100 }],
    () => {
      if (!useNew) {
        return fetchWpGraphQL(FaqsDocument, { first: 100 })
      }

      if (pageSlug) {
        const params = pageTitle ? `?title=${encodeURIComponent(pageTitle)}` : ''
        return backend<CoreFaq>(`faqs/public/for-page/${pageSlug}${params}`)
      }

      return backend<CoreFaq>(`faqs/public/${keyName}`)
    },
    { staleTime: 30000, retry: false, enabled: !!lookupSlug },
  )

  const faqListProps = { variant, hideTitle }

  if (useNew) {
    const core = data as CoreFaq | undefined
    const blocks = parseFaqMarkdown(core?.markdown || '').map(block =>
      block.type === 'header'
        ? block
        : { type: 'item' as const, question: block.question, answer: block.answer },
    )

    if (isFetching && fallbackFaqs?.length) {
      return <FaqList faqs={fallbackFaqs} {...faqListProps} />
    }

    if (!isFetching && (isError || !faqBlocksHaveItems(blocks)) && fallbackFaqs?.length) {
      return <FaqList faqs={fallbackFaqs} {...faqListProps} />
    }

    if (!faqBlocksHaveItems(blocks)) {
      return null
    }

    return <FaqList blocks={blocks} {...faqListProps} />
  }

  const wp = data as FaqsQuery | undefined
  const wpFaqs =
    wp?.faqs?.edges?.map(edge => ({
      question: edge?.node?.title || '',
      answer: <Html html={edge?.node?.acf?.answer || ''} />,
    })) || []

  if (!wpFaqs.length && fallbackFaqs?.length) {
    return <FaqList faqs={fallbackFaqs} {...faqListProps} />
  }

  return <FaqList faqs={wpFaqs} {...faqListProps} />
}

export default FaqSection
